// eslint-disable-next-line no-restricted-imports
import classNames from 'classnames';
import styled, { CSSProperties } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { socialPost } from '@sorare/core/src/style/mediaQuery';

export const buildGalleryGrid = <T,>(cards: T[], chunkSize = 4): T[][] => {
  const cardsToUse = [...cards];
  const result = [];
  const orders = [4, 5, 3, 6, 2, 7, 1, 8, 0, 9];

  if (cards.length === 0) {
    return [];
  }
  while (cardsToUse.length < orders.length * chunkSize) {
    cardsToUse.push(...cards);
  }

  for (let i = 0; i < orders.length; i += 1) {
    result[orders[i]] = cardsToUse.slice(i * chunkSize, (i + 1) * chunkSize);
  }

  return result;
};

const Grid = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;

  &.withShadowLayer {
    &:after {
      content: '';
      position: absolute;
      z-index: 2;
      inset: 0;
      background: linear-gradient(
        180deg,
        rgba(var(--c-rgb-neutral-100), 0.1) 20%,
        rgba(var(--c-rgb-neutral-100), 0.4) 40%,
        rgba(var(--c-rgb-neutral-100), 0.9) 90%
      );
    }
  }
`;
const Column = styled(Vertical)`
  &:nth-of-type(odd) {
    transform: translateY(calc(-1 * var(--triple-unit)));
  }
  &:nth-of-type(even) {
    transform: translateY(calc(-3 * var(--quadruple-unit)));
  }
`;
const Card = styled(ResponsiveImg)`
  filter: grayscale(100%);
  width: var(--width);

  @media ${socialPost} {
    width: 130px;
  }
`;

type Props = {
  className?: string;
  cards: { slug: string; pictureUrl: string | null }[];
  withShadowLayer?: boolean;
};

export const CardsGrid = ({ className, cards, withShadowLayer }: Props) => {
  const { up: isPostAndAbove } = useScreenSize('post');
  const {
    flags: { enableMyClubSplit = false },
  } = useFeatureFlags();

  const cardsGrid = buildGalleryGrid(
    cards,
    isPostAndAbove && !enableMyClubSplit ? 4 : 8
  );
  return (
    <Grid className={classNames(className, { withShadowLayer })}>
      {cardsGrid.map((column, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Column key={index}>
          {column.map((card, rowIndex) => (
            <Card
              // eslint-disable-next-line react/no-array-index-key
              key={rowIndex}
              src={card.pictureUrl || ''}
              alt={card.slug}
              cropWidth={160}
              style={
                {
                  '--width': enableMyClubSplit ? 60 : 160,
                } as CSSProperties
              }
            />
          ))}
        </Column>
      ))}
    </Grid>
  );
};
